import React from 'react';
import NavBar from './NavBar';
import { useNavigate } from 'react-router-dom';
import { auth, clearUserSessionData } from '../lib/firebase';
import { useEffect, useState } from 'react';

interface PageWrapperProps {
  children: React.ReactNode;
  title?: string;
  showBackToDashboard?: boolean;
  onBackToDashboard?: () => void;
  preserveNewSession?: boolean;
}

const PageWrapper: React.FC<PageWrapperProps> = ({ 
  children, 
  title,
  showBackToDashboard = true,
  onBackToDashboard,
  preserveNewSession = false
}) => {
  const [userName, setUserName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const storedName = localStorage.getItem('rawpa_user_name');
    if (storedName) setUserName(storedName);
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        const displayName = user.displayName || user.email?.split('@')[0] || 'User';
        setUserName(displayName);
        localStorage.setItem('rawpa_user_name', displayName);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      // Clear all user session data
      const userId = auth.currentUser?.uid;
      await clearUserSessionData(userId);
      
      // Sign out from Firebase
      await auth.signOut();
      
      // Redirect to signin page
      window.location.href = '/signin';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleBackToDashboard = () => {
    if (onBackToDashboard) {
      onBackToDashboard();
    } else if (preserveNewSession) {
      // Check if we're in a new session and preserve it
      const urlParams = new URLSearchParams(window.location.search);
      const isNewSession = urlParams.get('newSession') === 'true';
      if (isNewSession) {
        navigate('/dashboard?newSession=true');
      } else {
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      <NavBar 
        userName={userName} 
        onSignOut={handleSignOut}
        showBackToDashboard={showBackToDashboard}
        onBackToDashboard={handleBackToDashboard}
      />
      <div className="container mx-auto px-4 pt-[160px]">
        <MobileBackButton onBackToDashboard={handleBackToDashboard} />
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          {title && (
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-6">{title}</h1>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export const MobileBackButton: React.FC<{ onBackToDashboard: () => void }> = ({ onBackToDashboard }) => (
  <div className="lg:hidden mb-4">
    <button 
      onClick={onBackToDashboard}
      className="text-black opacity-60 hover:opacity-100 transition-opacity font-['Space_Grotesk']"
    >
      ← Back to Dashboard
    </button>
  </div>
);

export default PageWrapper; 